import React, { useState } from "react";
import { ChevronDown, ChevronUp } from "lucide-react";
import { PushAdminUser, PushDevice } from "@myapp/contract";
import { ListItem } from "../../../shared/ui/list/ListIteam/ListItem";
import Checkbox from "../../../shared/ui/controls/Checkbox/Checkbox";
import { IconButton } from "../../../shared/ui/icons/IconButton/IconButton";
import { getPositionsFromRoles } from "../../../entities/user";
import { pushUserColumns } from "../model/constants";
import { PushDeviceRow } from "./PushDeviceRow";

interface Props {
  user: PushAdminUser;
  checked: boolean;
  onToggle: (id: number) => void;
  onRevokeDevice: (user: PushAdminUser, device: PushDevice) => void;
  revokeDisabled?: boolean;
}

/**
 * The name is the only bold thing in the row — ListItem's cell default is bold primary, so
 * everything else opts out explicitly. A row nothing can be sent to renders muted, without
 * shouting "kein Gerät" down the whole column.
 */
export const PushUserRow: React.FC<Props> = ({
  user,
  checked,
  onToggle,
  onRevokeDevice,
  revokeDisabled,
}) => {
  const [expanded, setExpanded] = useState(false);
  const deviceCount = user.devices.length;
  const hasDevices = deviceCount > 0;

  return (
    <div className={hasDevices ? undefined : "opacity-60"}>
      <ListItem
        columns={pushUserColumns}
        actions={
          hasDevices ? (
            <IconButton
              icon={expanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
              variant="neutral"
              onClick={() => setExpanded((prev) => !prev)}
              aria-label={expanded ? "Geräte ausblenden" : "Geräte anzeigen"}
            />
          ) : undefined
        }
      >
        <ListItem.Cell
          title={hasDevices ? undefined : "Kein Gerät registriert"}
        >
          <Checkbox
            checked={checked}
            onChange={hasDevices ? () => onToggle(user.id) : undefined}
            disabled={!hasDevices}
            ariaLabel={`${user.name} als Empfänger auswählen`}
          />
        </ListItem.Cell>
        <ListItem.Cell>
          <div className="min-w-0">
            <p className="truncate">{user.name || "–"}</p>
            <p className="truncate text-[12px] font-normal text-gray-500">{user.email}</p>
          </div>
        </ListItem.Cell>
        {/* <p>, not <span>: ListItem's dark hover whitens only `div` and `p` — a span would
            keep its gray and vanish on the hovered background. */}
        <ListItem.Cell>
          <p className="font-normal text-gray-500">{getPositionsFromRoles(user.roles)}</p>
        </ListItem.Cell>
        <ListItem.Cell>
          {hasDevices ? (
            <p className="font-normal text-gray-600">
              {deviceCount} {deviceCount === 1 ? "Gerät" : "Geräte"}
            </p>
          ) : (
            <p className="font-normal text-gray-400">kein Gerät</p>
          )}
        </ListItem.Cell>
      </ListItem>

      {expanded && hasDevices && (
        // Left edge lines up with the name column (15px cell padding + 44px checkbox + 12px gap).
        <div className="ml-[71px] mr-3 mt-2 flex flex-col gap-1.5">

          {user.devices.map((device) => (
            <PushDeviceRow
              key={device.id}
              device={device}
              onRevoke={(d) => onRevokeDevice(user, d)}
              disabled={revokeDisabled}
            />
          ))}
        </div>
      )}
    </div>
  );
};
